import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConfigProvider, Modal, Button, Input, Spin, Tooltip } from 'antd';
import { FiSearch, FiLock, FiLink } from 'react-icons/fi';
import styles from './SidebarAccessModal.module.scss';
import {
    SIDEBAR_LINKS,
    SIDEBAR_ACCESS_ROLES,
    SIDEBAR_ACCESS_ALL,
    normalizeSidebarAccess,
} from '../../../constant/SidebarLinks';

const ROLE_KEYS = SIDEBAR_ACCESS_ROLES.map(r => r.key);

const withAll = (entry) => ({
    ...entry,
    [SIDEBAR_ACCESS_ALL]: ROLE_KEYS.every(r => Boolean(entry[r])),
});

const entryFor = (checked) => {
    const entry = { [SIDEBAR_ACCESS_ALL]: checked };
    ROLE_KEYS.forEach(r => { entry[r] = checked; });
    return entry;
};

const sameEntry = (a = {}, b = {}) => ROLE_KEYS.every(r => Boolean(a[r]) === Boolean(b[r]));

export const hostOf = (url) => {
    if (!url) return '';
    try {
        return new URL(url).host;
    } catch (e) {
        return String(url).replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    }
};

export const initialsOf = (name) =>
    String(name || '')
        .split(/[\s_-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('') || 'WL';

/** Light translucent version of a hex colour. */
export const tint = (hex, alpha = 0.12) => {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return `rgba(0, 0, 0, ${alpha})`;
    let h = m[1];
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const isHexColor = (v) => /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(v || '').trim());

/**
 * Colour used throughout the dialog: the primary colour saved for the clicked white label website.
 * Falls back to the current website theme only when that portal has no colour saved.
 */
export const usePortalColor = (customer) => {
    const websiteColor = useSelector(state => state.subDomain?.subDomainData?.primary_color);
    const portalColor = String(customer?.primary_color || '').trim();
    if (isHexColor(portalColor)) return portalColor.startsWith('#') ? portalColor : `#${portalColor}`;
    if (isHexColor(websiteColor)) return websiteColor;
    if (typeof window !== 'undefined') {
        const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--theme--Color').trim();
        if (cssVar) return cssVar;
    }
    return '#ab322d';
};

const keyToggle = (fn) => (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        fn();
    }
};

/** Checkbox built from spans so Materialize's global [type="checkbox"] rules cannot restyle it. */
export const Check = ({ checked, indeterminate = false, onChange, label, ariaLabel, color }) => {
    const toggle = () => onChange(!checked);
    const active = checked || indeterminate;
    return (
        <span
            role="checkbox"
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-label={ariaLabel || label}
            tabIndex={0}
            className={`${styles.check} ${checked ? styles.checkOn : ''} ${indeterminate ? styles.checkMixed : ''}`}
            onClick={toggle}
            onKeyDown={keyToggle(toggle)}
        >
            <span className={styles.checkBox} style={active ? { background: color, borderColor: color } : undefined}>
                <span className={styles.checkMark} />
            </span>
            {label ? <span className={styles.checkLabel}>{label}</span> : null}
        </span>
    );
};

/** Switch built from spans, for the same reason as Check (Materialize also restyles button:focus). */
export const Toggle = ({ checked, indeterminate = false, onChange, ariaLabel, color }) => {
    const toggle = () => onChange(!checked);
    return (
        <span
            role="switch"
            aria-checked={indeterminate ? 'mixed' : checked}
            aria-label={ariaLabel}
            tabIndex={0}
            className={`${styles.toggle} ${checked ? styles.toggleOn : ''} ${indeterminate ? styles.toggleMixed : ''}`}
            style={checked ? { background: color } : undefined}
            onClick={toggle}
            onKeyDown={keyToggle(toggle)}
        >
            <span className={styles.toggleKnob} />
        </span>
    );
};

/**
 * Admin dialog to choose, per sidebar link, which role groups can see it on a white label portal.
 *
 * Props:
 *  - open             boolean
 *  - customer         white label customer row (.id, .url, .name, .profile_logo, .homepage_logo, .sidebar_links_access)
 *  - onSubmit(access) receives the full access map; should return a promise
 *  - onCancel()
 */
const SidebarAccessModal = ({ open, customer, onSubmit, onCancel }) => {
    const [saved, setSaved] = useState({});
    const [access, setAccess] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const themeColor = usePortalColor(customer);

    useEffect(() => {
        if (open) {
            const initial = normalizeSidebarAccess(customer?.sidebar_links_access);
            setSaved(initial);
            setAccess(initial);
            setSubmitting(false);
            setSearch('');
        }
    }, [open, customer]);

    /* ---------- mutations ---------- */
    const setRole = (linkKey, role, checked) =>
        setAccess(prev => ({ ...prev, [linkKey]: withAll({ ...prev[linkKey], [role]: checked }) }));

    const setAll = (linkKey, checked) =>
        setAccess(prev => ({ ...prev, [linkKey]: entryFor(checked) }));

    const setColumn = (role, checked) =>
        setAccess(prev => {
            const next = {};
            SIDEBAR_LINKS.forEach(({ key }) => { next[key] = withAll({ ...prev[key], [role]: checked }); });
            return next;
        });

    const setEverything = (checked) => {
        const next = {};
        SIDEBAR_LINKS.forEach(({ key }) => { next[key] = entryFor(checked); });
        setAccess(next);
    };

    /* ---------- derived ---------- */
    const changedCount = useMemo(
        () => SIDEBAR_LINKS.filter(({ key }) => !sameEntry(access[key], saved[key])).length,
        [access, saved]
    );
    const hasChanges = changedCount > 0;

    const total = SIDEBAR_LINKS.length;
    const enabledCount = SIDEBAR_LINKS.filter(({ key }) => ROLE_KEYS.some(r => access[key]?.[r])).length;
    const roleCount = (role) => SIDEBAR_LINKS.filter(({ key }) => access[key]?.[role]).length;
    const columnChecked = (role) => roleCount(role) === total;
    const columnIndeterminate = (role) => !columnChecked(role) && roleCount(role) > 0;
    const everythingChecked = SIDEBAR_LINKS.every(({ key }) => access[key]?.[SIDEBAR_ACCESS_ALL]);
    const everythingIndeterminate = !everythingChecked && enabledCount > 0;

    const filteredLinks = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? SIDEBAR_LINKS.filter(({ label }) => label.toLowerCase().includes(q)) : SIDEBAR_LINKS;
    }, [search]);

    const isConfigured = customer?.sidebar_links_access !== null && customer?.sidebar_links_access !== undefined;
    const host = hostOf(customer?.url);
    const logo = customer?.profile_logo || customer?.homepage_logo;

    /* ---------- actions ---------- */
    const handleSave = async () => {
        setSubmitting(true);
        try {
            await onSubmit(access);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!submitting) onCancel();
    };

    return (
        <ConfigProvider theme={{ token: { colorPrimary: themeColor, borderRadius: 6, fontFamily: "'Poppins', sans-serif" } }}>
            <Modal
                className={styles.modal}
                style={{ '--portal-color': themeColor }}
                open={open}
                onCancel={handleCancel}
                width={820}
                centered
                maskClosable={!submitting}
                destroyOnHidden
                title={
                    <div className={styles.header}>
                        <div className={styles.avatar} style={{ background: tint(themeColor, 0.14), color: themeColor }}>
                            {logo ? <img src={logo} alt="" /> : initialsOf(customer?.name)}
                        </div>
                        <div className={styles.headerText}>
                            <div className={styles.headerTitle}>Sidebar Access</div>
                            <div className={styles.headerSub}>
                                {customer?.name ? <span className={styles.portalName}>{customer.name}</span> : null}
                                {customer?.name && host ? <span className={styles.sep}>·</span> : null}
                                {host ? <span>{host}</span> : null}
                                <Tooltip title="Primary colour set for this website">
                                    <span className={styles.swatch}>
                                        <span className={styles.swatchDot} style={{ background: themeColor }} />
                                        {themeColor.toUpperCase()}
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                        <span className={`${styles.badge} ${isConfigured ? styles.badgeCustom : styles.badgeDefault}`}>
                            {isConfigured ? 'Custom' : 'Default · all visible'}
                        </span>
                    </div>
                }
                footer={
                    <div className={styles.footer}>
                        <span className={`${styles.footerNote} ${hasChanges ? styles.footerNoteActive : ''}`}>
                            {hasChanges
                                ? `${changedCount} unsaved ${changedCount === 1 ? 'change' : 'changes'}`
                                : 'No unsaved changes'}
                        </span>
                        <div className={styles.footerButtons}>
                            <Button onClick={handleCancel} disabled={submitting}>Cancel</Button>
                            <Button type="primary" onClick={handleSave} loading={submitting} disabled={!hasChanges}>
                                Save changes
                            </Button>
                        </div>
                    </div>
                }
            >
                <Spin spinning={submitting}>
                    <div className={styles.toolbar}>
                        <Input
                            allowClear
                            className={styles.search}
                            classNames={{ input: 'browser-default' }}
                            prefix={<FiSearch className={styles.searchIcon} />}
                            placeholder="Search links"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <div className={styles.toolbarRight}>
                            <span className={styles.count}>
                                <strong style={{ color: themeColor }}>{enabledCount}</strong> of {total} links enabled
                            </span>
                            <span className={styles.vr} />
                            <Button type="link" size="small" onClick={() => setEverything(true)}>Select all</Button>
                            <Button type="link" size="small" onClick={() => setEverything(false)}>Clear all</Button>
                        </div>
                    </div>

                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.linkCol}>
                                        <span className={styles.thLabel}>Sidebar link</span>
                                    </th>
                                    <th className={styles.allCol}>
                                        <div className={styles.thStack}>
                                            <Check
                                                label="All"
                                                color={themeColor}
                                                checked={everythingChecked}
                                                indeterminate={everythingIndeterminate}
                                                onChange={setEverything}
                                            />
                                            <span className={styles.thCount}>{enabledCount}/{total}</span>
                                        </div>
                                    </th>
                                    {SIDEBAR_ACCESS_ROLES.map(({ key, label, members }) => (
                                        <th key={`col-${key}`} className={styles.roleCol}>
                                            <Tooltip title={`Applies to: ${members.join(', ')}`} mouseEnterDelay={0.4}>
                                                <div className={styles.thStack}>
                                                    <Check
                                                        label={label}
                                                        color={themeColor}
                                                        checked={columnChecked(key)}
                                                        indeterminate={columnIndeterminate(key)}
                                                        onChange={checked => setColumn(key, checked)}
                                                    />
                                                    <span className={styles.thCount}>{roleCount(key)}/{total}</span>
                                                </div>
                                            </Tooltip>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLinks.length === 0 && (
                                    <tr>
                                        <td colSpan={2 + SIDEBAR_ACCESS_ROLES.length} className={styles.empty}>
                                            <FiLink /> No links match "{search}"
                                        </td>
                                    </tr>
                                )}
                                {filteredLinks.map(({ key, label, icon: Icon }) => {
                                    const entry = access[key] || {};
                                    const enabled = ROLE_KEYS.some(r => entry[r]);
                                    const all = Boolean(entry[SIDEBAR_ACCESS_ALL]);
                                    const changed = !sameEntry(entry, saved[key]);
                                    return (
                                        <tr key={`row-${key}`} className={`${styles.row} ${enabled ? '' : styles.rowOff}`}>
                                            <td className={styles.linkCol}>
                                                <div className={styles.linkCell}>
                                                    <span
                                                        className={styles.linkIcon}
                                                        style={enabled ? { background: tint(themeColor), color: themeColor } : undefined}
                                                    >
                                                        {Icon ? <Icon /> : null}
                                                    </span>
                                                    <span className={styles.linkLabel}>{label}</span>
                                                    {!enabled && (
                                                        <span className={styles.lockTag}><FiLock /> Hidden</span>
                                                    )}
                                                    {changed && <span className={styles.changedDot} style={{ background: themeColor }} />}
                                                </div>
                                            </td>
                                            <td className={styles.allCol}>
                                                <Toggle
                                                    ariaLabel={`All roles for ${label}`}
                                                    color={themeColor}
                                                    checked={all}
                                                    indeterminate={!all && enabled}
                                                    onChange={checked => setAll(key, checked)}
                                                />
                                            </td>
                                            {SIDEBAR_ACCESS_ROLES.map(({ key: role, label: roleLabel }) => (
                                                <td key={`cell-${key}-${role}`} className={styles.roleCol}>
                                                    <Check
                                                        ariaLabel={`${roleLabel} for ${label}`}
                                                        color={themeColor}
                                                        checked={Boolean(entry[role])}
                                                        onChange={checked => setRole(key, role, checked)}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <p className={styles.help}>
                        Hidden links are removed from the sidebar and their pages cannot be opened by URL for that role.
                        Changes take effect the next time the portal is opened.
                    </p>
                </Spin>
            </Modal>
        </ConfigProvider>
    );
};

export default SidebarAccessModal;
